import type { CreateDocumentRequest } from '@palaccount/contracts';
import { FileDown, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { downloadDocument } from '@/features/documents/api';

/**
 * Wave 3B — the list's PDF act, for any of the record kinds the worker
 * renders (wave 2A's eight plus the invoice). ONE route only: the worker
 * re-reads the record and hands back the file (DECISION.md §2.13), so a
 * list that has only the row on hand can still print the document without
 * assembling a print payload of its own — which is the editor's job
 * (`DocumentActions`), and the editor's data.
 */
export function DocumentPdfButton({
  request,
  size = 'default',
  disabled,
}: {
  request: CreateDocumentRequest;
  size?: 'default' | 'sm' | undefined;
  disabled?: boolean | undefined;
}) {
  const { t } = useTranslation();
  const [state, setState] = useState<'idle' | 'working' | 'failed'>('idle');
  return (
    <Button
      type="button"
      variant="outline"
      size={size}
      disabled={disabled || state === 'working'}
      onClick={() => {
        setState('working');
        downloadDocument(request)
          .then(() => setState('idle'))
          .catch(() => setState('failed'));
      }}
    >
      {state === 'working' ? <Loader2 className="animate-spin" aria-hidden /> : <FileDown aria-hidden />}
      {state === 'failed' ? t('doclist.pdfFailed') : t('common.pdf')}
    </Button>
  );
}

/** The locale the worker renders in — the reader's language. */
export function documentLocale(language: string): 'ar' | 'en' {
  return language.startsWith('ar') ? 'ar' : 'en';
}
